import {
    deleteLibraryDocument,
    getLibraryDocument,
    ILibraryDocumentListItem,
    ILibraryListItem,
} from "@/api";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Download, Eye, FolderInput, Pencil, Trash2 } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { toast } from "sonner";

import { DeleteDocumentDialog } from "@/components/frontend/documents/DeleteDocumentDialog";
import { RenameDocumentDialog } from "@/components/frontend/documents/RenameDocumentDialog";
import { MoveToFolderModal } from "@/components/shared/move-to-folder";
import {
    RowActionsMenu,
    type RowAction,
} from "@/components/shared/RowActionsMenu";

interface DocumentActionsMenuProps {
    document: ILibraryDocumentListItem;
    onAction: () => void;
    readOnly: boolean;
}

export const DocumentActionsMenu = ({
    document,
    onAction,
    readOnly,
}: DocumentActionsMenuProps) => {
    const queryClient = useQueryClient();
    const [moveDialogOpen, setMoveDialogOpen] = useState(false);
    const [renameDialogOpen, setRenameDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const isSpreadsheet =
        document.file_type === "xlsx" || document.file_type === "csv";

    const { mutateAsync: viewDocument, isPending: isGettingDocumentLink } =
        useMutation({
            mutationFn: () => getLibraryDocument(document.id, true),
        });

    const handleOpenDocument = useCallback(() => {
        const tab = window.open("", "_blank");
        if (!tab) {
            toast.error(
                "Your browser blocked the new tab. Allow pop-ups and try again."
            );
            return;
        }
        tab.opener = null;
        void viewDocument()
            .then((doc) => {
                if (doc.download_url) {
                    tab.location.href = doc.download_url;
                } else {
                    tab.close();
                    toast.warning(
                        `${isSpreadsheet ? "Download" : "View"} link is not available for this document`
                    );
                }
            })
            .catch(() => {
                tab.close();
                toast.error(
                    `Failed to ${isSpreadsheet ? "download" : "view"} document`
                );
            });
    }, [isSpreadsheet, viewDocument]);

    // Parent-owned delete mutation (per Unit 6.IMPL bead + Unit 7 brief): the
    // menu owns the useMutation; the dialog receives `onConfirm` + `isDeleting`
    // props. Do NOT move this mutation into DeleteDocumentDialog.
    const { mutate: deleteDocument, isPending: isDeleting } = useMutation({
        mutationFn: () => deleteLibraryDocument(document.id),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ["frontend-library-list"],
            });
            queryClient.invalidateQueries({ queryKey: ["library-documents"] });
            queryClient.invalidateQueries({
                queryKey: ["frontend-library-folders"],
            });
            toast.success("Document deleted successfully");
            setDeleteDialogOpen(false);
            onAction();
        },
        onError: () => {
            toast.error("Failed to delete document");
        },
    });

    const actions = useMemo<RowAction[]>(() => {
        const fileAction: RowAction = {
            key: isSpreadsheet ? "download" : "view",
            label: isSpreadsheet ? "Download" : "View",
            icon: isSpreadsheet ? <Download /> : <Eye />,
            onSelect: handleOpenDocument,
            disabled: isGettingDocumentLink,
        };
        if (readOnly) return [fileAction];
        return [
            {
                key: "rename",
                label: "Rename",
                icon: <Pencil />,
                onSelect: () => setRenameDialogOpen(true),
            },
            fileAction,
            {
                key: "move",
                label: "Move",
                icon: <FolderInput />,
                onSelect: () => setMoveDialogOpen(true),
            },
            {
                key: "delete",
                label: "Delete",
                icon: <Trash2 />,
                variant: "destructive",
                onSelect: () => setDeleteDialogOpen(true),
            },
        ];
    }, [handleOpenDocument, isGettingDocumentLink, isSpreadsheet, readOnly]);

    const handleRenameComplete = () => {
        setRenameDialogOpen(false);
        onAction();
    };

    const handleMoveComplete = () => {
        setMoveDialogOpen(false);
        onAction();
    };

    return (
        <>
            <RowActionsMenu
                actions={actions}
                disabled={isGettingDocumentLink || isDeleting}
            />

            {!readOnly && (
                <>
                    <RenameDocumentDialog
                        open={renameDialogOpen}
                        onClose={() => setRenameDialogOpen(false)}
                        document={document}
                        onRenamed={handleRenameComplete}
                    />

                    <MoveToFolderModal
                        open={moveDialogOpen}
                        onClose={() => setMoveDialogOpen(false)}
                        items={[
                            {
                                type: "document",
                                id: document.id,
                                name: document.document_name,
                                library_folder_id: document.library_folder_id,
                                client_name: document.client_name,
                                client_id: document.client_id,
                                industry_name: document.industry_name,
                                carrier_name: document.carrier_name,
                                status: null,
                                clause_checklist_id: null,
                                clause_checklist_name: null,
                                item_count: 0,
                                fields_count: null,
                                parent_id: null,
                                is_knowledge_base: false,
                                created_at: document.created_at,
                                updated_at: null,
                                shared_by_organization_name: null,
                                shared_group_name: null,
                            } satisfies ILibraryListItem,
                        ]}
                        onMoved={handleMoveComplete}
                    />

                    <DeleteDocumentDialog
                        open={deleteDialogOpen}
                        onClose={() => setDeleteDialogOpen(false)}
                        document={document}
                        onConfirm={() => deleteDocument()}
                        isDeleting={isDeleting}
                    />
                </>
            )}
        </>
    );
};
